/**
 * AppDocumentManagement — Admin
 * Gestion de la base de connaissance Cygnus (.md)
 * CRUD complet : ajout, édition, suppression, ré-indexation.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { BookOpen, Plus, Edit2, Trash2, RefreshCw, Loader, Check, X, ChevronDown, ChevronRight, FileText } from 'lucide-react';
import {
  getCygnusDocuments, getCygnusDocumentContent,
  createCygnusDocument, updateCygnusDocument,
  deleteCygnusDocument, reindexCygnusDocument,
} from '../../api';

// ─── Formulaire ajout / édition ───────────────────────────────────────────────

const DocForm = ({ doc, onSave, onCancel }) => {
  const [form, setForm] = useState({
    name: doc?.name || '',
    slug: doc?.slug || '',
    content: doc?.content || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.content.trim()) {
      setError('Nom et contenu requis');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (doc) {
        await updateCygnusDocument(doc.id, form);
      } else {
        await createCygnusDocument(form);
      }
      onSave();
    } catch (e) {
      setError(e.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400';

  return (
    <div className="border border-indigo-200 rounded-xl bg-indigo-50/40 p-5 space-y-4">
      <div className="flex items-center gap-2 text-sm font-semibold text-indigo-700">
        <FileText size={15} />
        {doc ? 'Modifier le document' : 'Nouveau document'}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Nom du document *</label>
          <input className={inputCls} value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            placeholder="Ex: Guide stocks et commandes" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Slug (optionnel)</label>
          <input className={inputCls} value={form.slug}
            onChange={e => setForm(f => ({ ...f, slug: e.target.value }))}
            placeholder="Ex: stocks-commandes" />
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Contenu Markdown *
          <span className="ml-1 font-normal text-gray-400">— utilisez ## et ### pour structurer par sections</span>
        </label>
        <textarea
          className={inputCls + ' resize-y font-mono text-xs leading-relaxed'}
          rows={16}
          value={form.content}
          onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
          placeholder={`# Guide : Gestion des stocks\n\n## Ajouter un produit\n\nPour ajouter un produit, cliquez sur le bouton "+" en bas à droite...\n\n## Modifier le stock idéal\n\nLe stock idéal correspond à la quantité minimale souhaitée...`}
        />
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>
      )}

      <div className="flex justify-end gap-2">
        <button onClick={onCancel}
          className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg border border-gray-200">
          Annuler
        </button>
        <button onClick={handleSubmit} disabled={saving}
          className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-50">
          {saving ? <Loader size={14} className="animate-spin" /> : <Check size={14} />}
          {doc ? 'Mettre à jour' : 'Créer et indexer'}
        </button>
      </div>
    </div>
  );
};

// ─── Ligne document ────────────────────────────────────────────────────────────

const DocRow = ({ doc, onEdit, onDelete, onReindex }) => {
  const [expanded, setExpanded] = useState(false);
  const [loadingContent, setLoadingContent] = useState(false);
  const [content, setContent] = useState(null);
  const [reindexing, setReindexing] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleExpand = async () => {
    if (!expanded && content === null) {
      setLoadingContent(true);
      try {
        const res = await getCygnusDocumentContent(doc.id);
        setContent(res.data.content);
      } catch { setContent('Erreur de chargement'); }
      setLoadingContent(false);
    }
    setExpanded(e => !e);
  };

  const handleReindex = async () => {
    setReindexing(true);
    try {
      await reindexCygnusDocument(doc.id);
      onReindex();
    } catch {}
    setReindexing(false);
  };

  const handleDelete = async () => {
    if (!window.confirm(`Supprimer "${doc.name}" ?`)) return;
    setDeleting(true);
    try {
      await deleteCygnusDocument(doc.id);
      onDelete();
    } catch {}
    setDeleting(false);
  };

  const chunkCount = doc._count?.chunks ?? 0;

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-3 bg-white hover:bg-gray-50 transition-colors">
        <button onClick={handleExpand} className="text-gray-400 hover:text-gray-600">
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-800 text-sm truncate">{doc.name}</span>
            {doc.slug && (
              <span className="text-xs text-gray-400 font-mono bg-gray-100 px-1.5 py-0.5 rounded">{doc.slug}</span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-0.5">
            <span className="text-xs text-gray-400">
              {chunkCount} section{chunkCount !== 1 ? 's' : ''} indexée{chunkCount !== 1 ? 's' : ''}
            </span>
            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
              doc.indexed
                ? 'bg-green-100 text-green-700'
                : 'bg-yellow-100 text-yellow-700'
            }`}>
              {doc.indexed ? '✓ Indexé' : '⏳ En attente'}
            </span>
            <span className="text-xs text-gray-400">
              {new Date(doc.updatedAt).toLocaleDateString('fr-FR')}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          <button onClick={handleReindex} disabled={reindexing} title="Ré-indexer"
            className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition disabled:opacity-40">
            {reindexing ? <Loader size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          </button>
          <button onClick={() => onEdit(doc)} title="Modifier"
            className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition">
            <Edit2 size={14} />
          </button>
          <button onClick={handleDelete} disabled={deleting} title="Supprimer"
            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition disabled:opacity-40">
            {deleting ? <Loader size={14} className="animate-spin" /> : <Trash2 size={14} />}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-gray-100 bg-gray-50 px-4 py-3">
          {loadingContent ? (
            <div className="flex justify-center py-4"><Loader size={16} className="animate-spin text-indigo-500" /></div>
          ) : (
            <pre className="text-xs text-gray-600 font-mono whitespace-pre-wrap leading-relaxed max-h-60 overflow-y-auto">
              {content}
            </pre>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Composant principal ───────────────────────────────────────────────────────

const AppDocumentManagement = () => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [error, setError] = useState(null);

  const fetchDocs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getCygnusDocuments();
      setDocs(res.data || []);
    } catch { setError('Erreur de chargement'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const handleSave = () => {
    setShowForm(false);
    setEditingDoc(null);
    fetchDocs();
  };

  const handleEdit = async (doc) => {
    // Charger le contenu complet pour l'édition
    try {
      const res = await getCygnusDocumentContent(doc.id);
      setEditingDoc({ ...doc, content: res.data.content });
      setShowForm(true);
    } catch { setError('Erreur lors du chargement du document'); }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingDoc(null);
  };

  const totalSections = docs.reduce((acc, d) => acc + (d._count?.chunks || 0), 0);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <BookOpen size={16} className="text-indigo-500" />
            Base de connaissance — Cygnus
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-indigo-50 rounded-lg px-4 py-3 text-sm text-indigo-700 border border-indigo-100">
            <strong>Documents Markdown</strong> — Chaque document est découpé par sections (H2/H3) pour la recherche RAG.
            Utilisez <code className="bg-indigo-100 px-1 rounded text-xs">## Titre</code> et{' '}
            <code className="bg-indigo-100 px-1 rounded text-xs">### Sous-titre</code> pour structurer vos contenus.
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: 'Documents', value: docs.length },
              { label: 'Sections indexées', value: totalSections },
              { label: 'Indexés', value: docs.filter(d => d.indexed).length },
            ].map(s => (
              <div key={s.label} className="bg-gray-50 rounded-lg px-3 py-2 text-center">
                <div className="text-lg font-bold text-indigo-700">{s.value}</div>
                <div className="text-xs text-gray-500">{s.label}</div>
              </div>
            ))}
          </div>

          {/* Bouton ajouter */}
          {!showForm && (
            <button
              onClick={() => { setEditingDoc(null); setShowForm(true); }}
              className="flex items-center gap-2 px-4 py-2 border border-dashed border-indigo-300 rounded-xl text-sm text-indigo-600 hover:bg-indigo-50 w-full justify-center transition">
              <Plus size={16} />
              Ajouter un document
            </button>
          )}

          {/* Formulaire */}
          {showForm && (
            <DocForm doc={editingDoc} onSave={handleSave} onCancel={handleCancel} />
          )}

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>
          )}

          {/* Liste */}
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader className="animate-spin text-indigo-500" size={24} />
            </div>
          ) : docs.length === 0 ? (
            <div className="text-center py-10 text-gray-400">
              <BookOpen size={32} className="mx-auto mb-3 opacity-30" />
              <p className="text-sm">Aucun document. Ajoutez votre premier guide pour alimenter Cygnus.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {docs.map(doc => (
                <DocRow
                  key={doc.id}
                  doc={doc}
                  onEdit={handleEdit}
                  onDelete={fetchDocs}
                  onReindex={fetchDocs}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Conseils */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm text-gray-600">Conseils de rédaction</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="text-xs text-gray-600 space-y-2 leading-relaxed">
            <li><span className="font-medium text-gray-700">Structure</span> — Commencez par <code className="bg-gray-100 px-1 rounded"># Titre principal</code>, puis <code className="bg-gray-100 px-1 rounded">## Section</code> et <code className="bg-gray-100 px-1 rounded">### Sous-section</code>.</li>
            <li><span className="font-medium text-gray-700">Granularité</span> — Chaque section H2/H3 devient un chunk de recherche. Visez 100-400 mots par section.</li>
            <li><span className="font-medium text-gray-700">Langage</span> — Rédigez comme si vous répondiez à une question d'utilisateur. Évitez le jargon technique.</li>
            <li><span className="font-medium text-gray-700">Mise à jour</span> — Après chaque nouvelle fonctionnalité, mettez à jour le document correspondant et cliquez sur Ré-indexer.</li>
            <li><span className="font-medium text-gray-700">Slug</span> — Identifiant court et unique, optionnel. Ex: <code className="bg-gray-100 px-1 rounded">stocks-produits</code>.</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default AppDocumentManagement;
